import { Box, Button, CircularProgress, MobileStepper, Typography, useTheme } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';
import { XOctagon } from 'lucide-react';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { isValidDecimal, isValidInteger, isValidMeanWeight, isValidValue } from 'utils/validate-numbers';
import { postTransfer } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import {
  CreateDestinationPondInput,
  CreateTransferInput,
} from 'views/production-systems/features/farm-overview/types/create-transfer-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Pond, PondStatus } from 'views/production-systems/types/pond.type';
import { Transfer } from 'views/production-systems/types/transfer.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import { getCycleName } from 'views/production-systems/utils/get-cycle-name';
import TransferFirstStep from './transfer-first-step';

import RegistrationHeader from 'views/production-systems/components/registration-header';
import { DestinationRow } from 'views/production-systems/features/farm-overview/types/destination-row.type';
import SourcePonds from 'views/production-systems/features/farm-overview/components/add-transfer/source-ponds';
import DuplicateRegistrationWarning, {
  REGISTRATION_EXISTS_QUERY_KEY,
} from 'views/production-systems/features/farm-overview/components/duplicate-registration-warning';
import { RegistrationActionType } from 'views/production-systems/features/farm-overview/types/registration-action-type.enum';

type Props = {
  handleClose?: () => void;
  selectedPond: Pond | undefined;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.transfer';

const AddTransfer: React.FC<Props> = ({ handleClose, selectedPond }) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const sourceDataGridApiRef = useGridApiRef();
  const snackbarDispatch = useSnackbarDispatch();
  const destinationDataGridApiRef = useGridApiRef();
  const { lockedPonds, releasePonds, globalSiteId, lockPonds, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  // #region STATES

  const [confirm, setConfirm] = useState<boolean>(false);
  const [destinationPonds, setDestinationPonds] = useState<Pond[]>([]);
  const [destinationRows, setDestinationRows] = useState<DestinationRow[]>([]);
  const [showErrorMessage, setErrorMessage] = useState<boolean>(false);
  const [isPartialTransfer, setIsPartialTransfer] = useState<boolean>(false);
  const sourcePond = useMemo(() => selectedPond as Pond, [selectedPond]);

  //#endregion STATES

  //#region Source Data Grid

  const [sourceColumnVisibility, setSourceColumnVisibility] = useState<GridColumnVisibilityModel>({
    pondName: true,
    pondId: true,
    cycleName: true,
    currentQty: true,
    currentMw: true,
    currentBm: true,

    newCycleName: isPartialTransfer,
    remainingQty: isPartialTransfer,
    remainingMw: isPartialTransfer,
    remainingBm: isPartialTransfer,
  });

  useEffect(() => {
    if (!!sourceDataGridApiRef.current) {
      setSourceColumnVisibility({
        ...sourceColumnVisibility,
        newCycleName: isPartialTransfer,
        remainingQty: isPartialTransfer,
        remainingMw: isPartialTransfer,
        remainingBm: isPartialTransfer,
      });
    }
  }, [isPartialTransfer, sourceDataGridApiRef]);

  const sourceInitialRow = useMemo(() => {
    const currentBm = calculateBiomass(sourcePond.currentMw, sourcePond.currentQty);
    return {
      pondName: sourcePond.pondName,
      pondId: sourcePond.pondId,
      cycleName: sourcePond.cycleName,
      currentQty: sourcePond.currentQty,
      currentMw: sourcePond.currentMw,
      currentBm: currentBm,
      cycleId: sourcePond.cycleId,
      newCycleName: sourcePond.cycleName,
      remainingQty: sourcePond.currentQty,
      remainingMw: sourcePond.currentMw,
      remainingBm: currentBm,
    };
  }, [sourcePond]);

  const [sourceRow, setSourceRow] = useState(sourceInitialRow || []);

  //#endregion

  //#region Destination Data Grid

  useEffect(() => {
    setDestinationRows(
      destinationPonds.map((pond) => ({
        pondName: pond.pondName,
        pondId: pond.pondId,
        cycleId: pond.cycleId,
        cycleName: pond.pondStatus === PondStatus.active ? pond.cycleName : getCycleName(pond.pondName, registrationDate),
        currentQty: pond.currentQty,
        transferQty: 0,
        transferMw: 0,
        transferBm: 0,
      }))
    );
  }, [destinationPonds]);

  const destinationProcessRowUpdate = (newRow: GridRowModel<DestinationRow>) => {
    const updatedRow = { ...newRow, transferBm: calculateBiomass(newRow.transferMw, newRow.transferQty) };
    setDestinationRows(destinationRows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const destinationColumns: GridColDef[] = [
    {
      field: 'pondName',
      flex: 2,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.destinationPond` }),
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}>
            {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={row.pondName}>
              {row.pondName}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'cycleName',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.cycleName` }),
      editable: false,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'currentQty',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.currentQty` }),
      editable: false,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'transferQty',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.transferQty` }),
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value === 0 || !isValidInteger(params.props.value) || isValidValue(params.props.value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-transferQty`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'transferMw',
      flex: 1,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.transferMw` }),
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const hasError = !isValidDecimal(value) || isValidValue(value) || !isValidMeanWeight(value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-transferMw`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'transferBm',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destination.transferBm` }),
      flex: 1,
      type: 'number',
      editable: false,
      align: 'right',
      headerAlign: 'right',
    },
  ];

  useEffect(() => {
    if (isPartialTransfer) {
      const qty = destinationRows.reduce((prev, curr) => prev + curr.transferQty, 0);
      const newRemainingQty = sourcePond.currentQty - qty;
      setSourceRow((row) => ({
        ...row,
        remainingQty: newRemainingQty,
        remainingBm: calculateBiomass(row.remainingMw, newRemainingQty),
      }));
    }
  }, [destinationRows, isPartialTransfer]);

  //#endregion

  // #region FORMS

  const summaryValues: SummaryItem[] = useMemo(() => {
    let summary = destinationRows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.transferMw, curr.transferQty);

        return {
          transferQty: Number(prev.transferQty) + Number(curr.transferQty),
          transferBm: Number(prev.transferBm) + biomass,
          transferMw: 0,
        };
      },
      {
        transferQty: 0,
        transferMw: 0,
        transferBm: 0,
      }
    );

    summary.transferMw = calculateMeanWeight(summary.transferBm, summary.transferQty);

    return [
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: summary.transferQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.transferMw, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 2, // Derived from .toFixed(2)
      },
      {
        value: summary.transferBm, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 2, // Derived from .toFixed(2)
      },
    ];
  }, [destinationRows]);

  // #region CELLS

  // #region POST DATA

  const createTransfer = usePost<Transfer, { globalSiteId: string; data: CreateTransferInput }>(
    (request) => {
      request.data.isPartialTransfer = isPartialTransfer;

      if (!request.data.isPartialTransfer) {
        request.data.endQty = 0; // full transfer always empties the source pond
      }

      return postTransfer(request.globalSiteId, request.data);
    },
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #region HANDLERS

  const handleSubmit = () => {
    let destinationRowsData: CreateDestinationPondInput[] = destinationRows.map((row) => ({
      pondId: row.pondId,
      previousCycleId: row.cycleId,
      newCycleName: row.cycleName,
      number: row.transferQty,
      mw: row.transferMw,
    }));

    let data: CreateTransferInput = {
      destinationPonds: destinationRowsData,
      newCycleName: sourceRow.newCycleName,
      sourcePondId: sourceRow.pondId,
      date: registrationDate.format('YYYY-MM-DD'),
      cycleId: sourceRow.cycleId,
      endQty: sourceRow.currentQty,
      endMw: sourceRow.currentMw,
      isPartialTransfer: isPartialTransfer,
      remainingQty: sourceRow.remainingQty,
      remainingMw: sourceRow.remainingMw,
    };

    createTransfer.mutate({
      globalSiteId: globalSiteId,
      data: data,
    });
  };

  // #region RENDERS

  //#endregion RENDERS

  //#region HOOKS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    if (!destinationPonds.every((v) => lockedPonds.granted.includes(v.pondId))) lockPonds(destinationPonds.map((pond) => pond.pondId));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    releasePonds(destinationPonds.map((p) => p.pondId));
    setDestinationPonds([]);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // add check to disable submit if no feed and feeder
  const disableSubmit = useMemo(() => {
    if (isEditing || hasError) return true;

    return destinationRows.some((row) => {
      const validatedRows =
        row.transferQty <= 0 || (!isValidInteger(row.transferQty) && row.currentQty <= 0) || !isValidDecimal(row.transferMw);
      return validatedRows;
    });
  }, [destinationRows, isPartialTransfer, isEditing, hasError]);

  //#endregion HOOKS

  return !!lockedPonds?.notGranted.length && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <TransferFirstStep destinationPonds={destinationPonds} sourcePond={sourcePond} setDestinationPonds={setDestinationPonds} />
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.Transfer} ponds={[sourcePond, ...destinationPonds]} />
          <RegistrationHeader />
          <SourcePonds
            selectedPond={selectedPond}
            sourceRow={sourceRow}
            setSourceRow={setSourceRow}
            isPartialTransfer={isPartialTransfer}
            setIsPartialTransfer={setIsPartialTransfer}
            destinationRows={destinationRows}
          />

          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <FormattedMessage id={`${PAGE_NAME_ID}.destinationPonds`} />
            <DataGrid
              rows={destinationRows}
              columns={destinationColumns}
              getRowId={(row) => row.pondId}
              onCellEditStop={handleEditing}
              apiRef={destinationDataGridApiRef}
              showToolbar
              processRowUpdate={destinationProcessRowUpdate}
              slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
              slotProps={{
                toolbar: {
                  csvOptions: {
                    fileName: `${intl.formatMessage({
                      id: 'productionSystems.registrationLog.registrationTypes.transfer',
                    })}-${dayjs().format('DD/MM/YYYY')}`,
                  },
                },
                footer: {
                  summaryValues: summaryValues,
                } as DataGridSummaryProps,
              }}
              onRowClick={({ id }) => {
                const { field } = destinationDataGridApiRef.current?.state.tabIndex.cell ?? {};
                destinationDataGridApiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
              }}
            />
          </Box>

          <Box
            mb={10}
            padding={2}
            marginTop={2}
            display={showErrorMessage ? 'flex' : 'none'}
            maxWidth={650}
            alignItems={'center'}
            justifyContent={'center'}
            borderRadius={4}
            borderColor={theme.palette.red.o50}>
            <XOctagon size={80} />
            <Typography ml={8}>
              <FormattedMessage id={`${PAGE_NAME_ID}.partialTransferError`} />
            </Typography>
          </Box>
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createTransfer.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createTransfer.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1 || destinationPonds.length === 0}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

export default React.memo(AddTransfer);
